import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import Footer from '@/components/feature/Footer';
import { MAIN_SITE_URL } from '@/mocks/categories';
import { PILLARS } from '@/mocks/pillars';
import JsonLd from '@/seo/JsonLd';
import { SITE_URL, SITE_NAME, FAVICON_URL } from '@/seo/schema';

export default function PillarPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const pillar = PILLARS.find((p) => p.slug === slug);

  useEffect(() => {
    const html = document.documentElement;
    const prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prevScrollBehavior;
  }, [slug]);

  if (!pillar) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background-50 px-4 text-center">
        <h1 className="font-heading text-2xl font-bold text-foreground-950">Guide not found</h1>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          {t('pillar.back_home')}
        </Link>
      </div>
    );
  }

  const sections = [1, 2, 3, 4];
  const tips = [1, 2, 3];
  const faqs = [1, 2, 3];

  const pageUrl = `${SITE_URL}/article/${pillar.slug}`;

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: t(`pillars.${pillar.slug}.title`),
    description: t(`pillars.${pillar.slug}.meta`),
    image: pillar.image,
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    mainEntityOfPage: pageUrl,
    datePublished: '2026-08-23',
    dateModified: '2026-08-23',
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('pillar.breadcrumb_home'), item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: t(`pillars.${pillar.slug}.breadcrumb`), item: pageUrl },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((n) => ({
      '@type': 'Question',
      name: t(`pillars.${pillar.slug}.faq_q${n}`),
      acceptedAnswer: { '@type': 'Answer', text: t(`pillars.${pillar.slug}.faq_a${n}`) },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleLd} />
      <JsonLd data={breadcrumbLd} />
      <JsonLd data={faqLd} />
      <header className="sticky top-0 z-40 border-b border-background-200 bg-white">
        <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-list-3-line text-lg" />
            </span>
            <span className="font-heading text-base font-bold text-foreground-900 whitespace-nowrap">
              USFans <span className="text-primary-600">Spreadsheet</span>
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden items-center gap-1.5 rounded-full border border-background-300/70 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 sm:flex whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              {t('pillar.back_home')}
            </Link>
            <LanguageSwitcher />
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:flex whitespace-nowrap cursor-pointer"
            >
              {t('nav.visit')}
              <i className="ri-arrow-right-up-line" />
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="Breadcrumb">
          <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
            {t('pillar.breadcrumb_home')}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-700">{t(`pillars.${pillar.slug}.breadcrumb`)}</span>
        </nav>

        <article className="mt-6">
          <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {t(`pillars.${pillar.slug}.title`)}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" />
              {t(`pillars.${pillar.slug}.read_time`)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-refresh-line" />
              {t('pillar.updated')}: 2026
            </span>
          </div>

          <div className="mt-6 overflow-hidden rounded-xl border border-background-200/70 bg-background-100">
            <img
              src={pillar.image}
              alt={t(`pillars.${pillar.slug}.title`)}
              title={`${t(`pillars.${pillar.slug}.breadcrumb`)} — USFans Spreadsheet`}
              className="h-52 w-full object-cover object-top md:h-64"
            />
          </div>

          <p className="mt-8 text-base leading-relaxed text-foreground-700">{t(`pillars.${pillar.slug}.intro`)}</p>

          {sections.map((n) => (
            <div key={n}>
              <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {t(`pillars.${pillar.slug}.h2_${n}`)}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{t(`pillars.${pillar.slug}.p_${n}`)}</p>
            </div>
          ))}

          <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('pillar.tip_heading')}
          </h2>
          <ul className="mt-4 space-y-2.5">
            {tips.map((n) => (
              <li key={n} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-check-line text-sm" />
                </span>
                {t(`pillars.${pillar.slug}.tip_${n}`)}
              </li>
            ))}
          </ul>

          <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {t('pillar.faq_heading')}
          </h2>
          <div className="mt-4 space-y-3">
            {faqs.map((n) => {
              const isOpen = openFaq === n - 1;
              return (
                <div key={n} className="overflow-hidden rounded-xl border border-background-200/70 bg-background-50">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : n - 1)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-900">{t(`pillars.${pillar.slug}.faq_q${n}`)}</span>
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    >
                      <i className="ri-arrow-down-s-line" />
                    </span>
                  </button>
                  {isOpen && (
                    <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{t(`pillars.${pillar.slug}.faq_a${n}`)}</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 p-8 text-background-50">
            <h2 className="font-heading text-xl font-bold md:text-2xl">{t(`pillars.${pillar.slug}.cta_title`)}</h2>
            <p className="mt-3 text-sm leading-relaxed text-background-50/90">{t(`pillars.${pillar.slug}.cta_body`)}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              {t(`pillars.${pillar.slug}.cta_button`)}
              <i className="ri-arrow-right-up-line" />
            </a>
          </div>
        </article>

        <div className="mt-10">
          <h3 className="font-heading text-base font-bold text-foreground-950">{t('pillar.related_title')}</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {pillar.related.map((rel) => (
              <Link
                key={rel}
                to={`/article/${rel}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-background-300/70 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                {t(`pillars.${rel}.breadcrumb`)}
                <i className="ri-arrow-right-line" />
              </Link>
            ))}
          </div>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            {t('pillar.back_home')}
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}